import OverflowMenuVertical32 from '@carbon/icons-react/es/overflow-menu--vertical/32';
import { isEmpty } from 'lodash';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  ProjectDetailTabKeys,
  ProjectDetailTabPropTypes,
  tabApiEnumMap,
} from 'api/project';
import {
  CompactMenu,
  CompactMenuTitle,
} from 'components/atoms/menu/compact-menu';
import {
  useFeatureEnabledProjectTabs,
  useProjectVisibilityConfig,
} from 'components/molecules/project-details/hooks';
import { ConversationMenuItem } from 'components/organisms/projects/project-conversation';
import { PermissionRuleName } from 'constant';
import { useProjectDetail } from 'data/projects';
import { selectStatusUpdates } from 'data/status-updates';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useUser } from 'hooks/use-user';
import { useProjectSpacesQuery } from 'queries';
import { useQueryFormInstanceByProjectKey } from 'queries/form-instance';
import { VisibilitySettings } from '../visibility/visibility-settings';
import { CompactExpandMenu, ExpandedIcon } from './tabs.styles';
import { useTabActions } from './use-tab-actions';

const getIcon = (IconEl) => {
  return IconEl ? <IconEl /> : null;
};

export const MenuLink = styled(Link)`
  color: var(--gray-70) !important;
`;

export const ProjectDetailTabs: FC<ProjectDetailTabPropTypes> = ({
  projectId,
  className,
  activeTabKey,
}) => {
  const { getPathName } = useTabActions(projectId);

  const featureEnabledprojectTabs = useFeatureEnabledProjectTabs();

  const config = useProjectVisibilityConfig(projectId);

  const projectVisibleTabs = featureEnabledprojectTabs.filter((tab) => {
    if (config && !isEmpty(config)) {
      // always show overview tab
      return (
        tab.id === ProjectDetailTabKeys.Overview ||
        config.visibilityMeta?.[tabApiEnumMap[tab.id]]
      );
    }

    return true;
  });
  const { data: formResponse } = useQueryFormInstanceByProjectKey(projectId);
  const { data: projectSpacesList } = useProjectSpacesQuery(projectId);
  const statusUpdates = useSelector(selectStatusUpdates);

  const projectTabs = projectVisibleTabs.map((tab) => {
    // Show different component for chat icon
    if (tab.id === ProjectDetailTabKeys.Chat) {
      return {
        ...tab,
        titleContent: <ConversationMenuItem projectId={projectId} />,
      };
    }

    if (tab.id === ProjectDetailTabKeys.Spaces) {
      return {
        ...tab,
        ruleName:
          (projectSpacesList ?? []).length > 0 ? undefined : tab.ruleName,
        titleContent: <FormattedMessage id={tab.translationKey} />,
      };
    }

    if (tab.id === ProjectDetailTabKeys.FormResponses) {
      return {
        ...tab,
        ruleName:
          (formResponse?.instances ?? []).length > 0 ? undefined : tab.ruleName,
        titleContent: <FormattedMessage id={tab.translationKey} />,
      };
    }
    if (tab.id === ProjectDetailTabKeys.StatusUpdate) {
      return {
        ...tab,
        ruleName: statusUpdates.length > 0 ? undefined : tab.ruleName,
        titleContent: <FormattedMessage id={tab.translationKey} />,
      };
    }

    return {
      ...tab,
      titleContent: <FormattedMessage id={tab.translationKey} />,
    };
  });

  const loggedInUser = useUser();
  const { data: project } = useProjectDetail(projectId);
  const isProjectOwner =
    loggedInUser.user?.userId === project?.projectOwner.userId;
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const showVisibilityChanger =
    isAllowedForCurrentUser(
      PermissionRuleName.ProjectChangeTabVisibilityAction,
    ) || isProjectOwner;

  return (
    <CompactMenu
      overflowedIndicator={<ExpandedIcon />}
      mode="horizontal"
      className={className}
      selectedKeys={activeTabKey ? [activeTabKey] : undefined}
      data-test-id="project-header-tabs"
    >
      {projectTabs.map((tab) => {
        const path = getPathName(tab.id as ProjectDetailTabKeys);

        return (
          <CompactMenu.ItemWithPermission
            key={tab.id}
            data-test-id={`project-${tab.id}-tab`}
            data-tab-id={tab.ruleName}
            ruleName={tab.ruleName}
          >
            <MenuLink to={path}>
              <CompactMenuTitle
                text={tab.titleContent}
                icon={getIcon(tab.icon)}
              />
            </MenuLink>
          </CompactMenu.ItemWithPermission>
        );
      })}
      {showVisibilityChanger ? (
        <CompactExpandMenu
          key="visibilitySubMenu"
          icon={
            <OverflowMenuVertical32
              style={{ paddingBottom: 'var(--spacing-05' }}
            />
          }
          popupClassName="visibility-submenu"
        >
          <CompactMenu.Item key="visibilitySubMenuItem">
            <VisibilitySettings
              projectId={projectId}
              activeTabKey={activeTabKey}
            />
          </CompactMenu.Item>
        </CompactExpandMenu>
      ) : null}
    </CompactMenu>
  );
};
